// Live running trains — a small extra source/layer, independent of the layer
// registry: these aren't Features (no detail view, no search, no export), just an
// ephemeral overlay polled from our own Worker (worker/index.js proxies Irish Rail's
// realtime API, which has no CORS headers of its own). Toggled by `liveTrainsOn`.
import type { GeoJSONSource, Map as MLMap } from "maplibre-gl";
import { Popup } from "maplibre-gl";
import { LIVE_TRAIN_ICON } from "./icons";
import { liveTrainsCount, liveTrainsError } from "../state/store";

export const LIVE_SRC = "live-trains";
const LIVE_LAYER = "live-trains-points";
const POLL_MS = 20000;

interface LiveTrain {
  code: string;
  status: string; // N = not yet departed, R = running, T = terminated
  lat: number;
  lon: number;
  direction?: string;
  message?: string;
}

const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

function toGeoJSON(trains: LiveTrain[]): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: trains.map((t) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [t.lon, t.lat] },
      properties: { code: t.code, status: t.status, direction: t.direction ?? "", message: t.message ?? "" },
    })),
  };
}

/** Add the source + layer (idempotent). Call after every style load, like ensureOverlays. */
export function ensureLiveTrainsOverlay(map: MLMap): void {
  if (!map.getSource(LIVE_SRC)) {
    map.addSource(LIVE_SRC, { type: "geojson", data: EMPTY });
  }
  if (!map.getLayer(LIVE_LAYER)) {
    map.addLayer({
      id: LIVE_LAYER,
      type: "symbol",
      source: LIVE_SRC,
      layout: {
        "icon-image": LIVE_TRAIN_ICON,
        "icon-size": ["interpolate", ["linear"], ["zoom"], 6, 0.6, 10, 0.85, 13, 1],
        "icon-allow-overlap": true,
      },
    });
    let popup: Popup | null = null;
    map.on("click", LIVE_LAYER, (e) => {
      const feat = e.features?.[0];
      if (!feat) return;
      const p = feat.properties as { code?: string; direction?: string; status?: string; message?: string };
      const coords = (feat.geometry as GeoJSON.Point).coordinates.slice() as [number, number];
      const statusLabel = p.status === "R" ? "Running" : p.status === "T" ? "Terminated" : "Not yet departed";
      popup?.remove();
      popup = new Popup({ closeButton: true, offset: 12 })
        .setLngLat(coords)
        .setHTML(`<strong>${p.code ?? "Train"}</strong><br>${p.direction ?? ""}<br><span class="muted">${statusLabel}</span>`)
        .addTo(map);
    });
    map.on("mouseenter", LIVE_LAYER, () => { map.getCanvas().style.cursor = "pointer"; });
    map.on("mouseleave", LIVE_LAYER, () => { map.getCanvas().style.cursor = ""; });
  }
}

let mapRef: MLMap | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let inFlight = false;

async function poll(): Promise<void> {
  if (inFlight || !mapRef) return;
  inFlight = true;
  try {
    const res = await fetch("/api/trains/live", { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as { trains: LiveTrain[] };
    liveTrainsError.value = null;
    liveTrainsCount.value = data.trains.length;
    const src = mapRef.getSource(LIVE_SRC) as GeoJSONSource | undefined;
    src?.setData(toGeoJSON(data.trains));
  } catch (err) {
    liveTrainsError.value = err instanceof Error ? err.message : String(err);
  } finally {
    inFlight = false;
  }
}

function schedule(): void {
  timer = setTimeout(async () => {
    if (document.visibilityState === "visible") await poll();
    schedule();
  }, POLL_MS);
}

/** Start polling for the given map. Safe to call repeatedly (e.g. after a style swap). */
export function startLiveTrains(map: MLMap): void {
  mapRef = map;
  poll();
  if (!timer) schedule();
}

export function stopLiveTrains(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  liveTrainsCount.value = 0;
  liveTrainsError.value = null;
  const src = mapRef?.getSource(LIVE_SRC) as GeoJSONSource | undefined;
  src?.setData(EMPTY);
}
